import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import SlidePanel from "./SlidePanel";
import PendingEdits from "./PendingEdits";
import UserLink from "./UserLink";
import RejectForm from "./RejectForm";
import { REPORT_REASON_LABELS, MAX_RESOLUTION_NOTE } from "../helpers/reportRules";
import { timeAgo } from "../helpers/timeAgo";

const known = (v) => v !== undefined && v !== null && v !== "" && v !== "unknown" && v !== 0 && v !== "0";

function PendingShip({ ship, busy, onReview, onOpenUser }) {
  const [open, setOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const meta = ship.image_meta || {};
  const facts = [ship.type, ship.date, known(ship.imo) && `IMO ${ship.imo}`].filter(known);
  const desc = ship.description || "";
  const long = desc.length > 280;

  return (
    <div className="panel-item review-card">
      <div className="review-card-top">
        <div className="panel-thumb">
          {ship.images?.[0] ? <img src={ship.images[0]} alt="" loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} /> : <span>⚓</span>}
        </div>
        <div className="panel-item-main">
          <h3>{ship.title || "Untitled"}</h3>
          <div className="panel-meta">
            <span className="status-badge pending">#{ship.id}</span>
            <span>by {ship.submitted_by ? <UserLink username={ship.submitted_by} onOpen={onOpenUser} /> : "unknown"}</span>
          </div>
          {facts.length > 0 && <p className="panel-hint">{facts.join(" · ")}</p>}
          <p className="panel-hint">📍 {Number(ship.lat).toFixed(4)}, {Number(ship.lng).toFixed(4)}</p>
        </div>
      </div>

      {desc && (
        <div className="review-desc">
          <div className={open || !long ? "" : "review-desc-clamp"}><ReactMarkdown>{desc}</ReactMarkdown></div>
          {long && <button className="panel-link" onClick={() => setOpen(!open)}>{open ? "Show less" : "Read more"}</button>}
        </div>
      )}

      {ship.images?.length > 0 && (
        <div className="review-photos">
          {ship.images.map((u) => (
            <a key={u} href={u} target="_blank" rel="noreferrer" title={[meta[u]?.credit, meta[u]?.license].filter(Boolean).join(" · ") || "Open photo"}>
              <img src={u} alt="" loading="lazy" onError={(e) => (e.currentTarget.style.opacity = 0.2)} />
            </a>
          ))}
        </div>
      )}
      {ship.photo_attribution && <p className="panel-hint"><strong>Photo attribution:</strong> {ship.photo_attribution}</p>}

      {ship.links?.length > 0 && (
        <ul className="review-links">
          {ship.links.map((l) => <li key={l}><a href={l} target="_blank" rel="noreferrer">{l.replace(/^https?:\/\/(www\.)?/, "")}</a></li>)}
        </ul>
      )}

      {rejecting ? (
        <RejectForm what={`“${ship.title || "Untitled"}”`} busy={busy} onCancel={() => setRejecting(false)} onConfirm={(r) => onReview(ship.id, "reject", r)} />
      ) : (
        <div className="review-actions">
          <button className="review-btn approve" disabled={busy} onClick={() => onReview(ship.id, "approve")}>✓ Approve</button>
          <button className="review-btn reject" disabled={busy} onClick={() => setRejecting(true)}>✕ Reject</button>
        </div>
      )}
    </div>
  );
}


// A problem report on a published wreck. Resolve = "you were right, it's dealt with", Dismiss = "no change needed".
// Either way the reporter is notified, with the optional note below.
function ReportCard({ report, ship, busy, onReview, onViewShip, onOpenUser }) {
  const [note, setNote] = useState("");
  const [all, setAll] = useState(false);
  const act = (action) => onReview(report.id, { action, note: note.trim(), all: report.others > 0 && all });

  return (
    <div className="panel-item review-card">
      <div className="review-card-top">
        <div className="panel-thumb">
          {report.ship_image ? <img src={report.ship_image} alt="" loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} /> : <span>⚓</span>}
        </div>
        <div className="panel-item-main">
          <h3>{report.ship_title}</h3>
          <div className="panel-meta">
            <span className="status-badge pending">#{report.ship_id}</span>
            <span>by {report.reporter ? <UserLink username={report.reporter} onOpen={onOpenUser} /> : "unknown"}</span>
            <span>{timeAgo(report.created)}</span>
          </div>
          {report.others > 0 && <p className="panel-hint report-others">⚑ {report.others} other open report{report.others === 1 ? "" : "s"} on this wreck</p>}
        </div>
      </div>

      <p className="report-reason"><strong>{REPORT_REASON_LABELS[report.reason] || report.reason}</strong></p>
      {report.note && <p className="report-note">{report.note}</p>}

      <div className="report-links">
        {ship && <button type="button" className="panel-link" onClick={() => onViewShip(ship)}>Show on map →</button>}
      </div>

      <label className="report-field">
        <span>Message to the reporter <em>(optional)</em></span>
        <textarea rows={2} maxLength={MAX_RESOLUTION_NOTE} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Thanks, I've moved the pin." />
      </label>
      {report.others > 0 && (
        <label className="report-check">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          <span>Do the same for the {report.others} other open report{report.others === 1 ? "" : "s"} on this wreck</span>
        </label>
      )}

      <div className="review-actions">
        <button className="review-btn approve" disabled={busy} onClick={() => act("resolve")}>✓ Resolve</button>
        <button className="review-btn" disabled={busy} onClick={() => act("dismiss")}>Dismiss</button>
      </div>
    </div>
  );
}

// Admin review queue: new ships and suggested edits, with counts, search and one-click approve/reject.
export default function PendingPanel({ ships, edits, reports = [], mapShips = [], loaded, busyId, onRefresh, onReviewShip, onReviewEdit, onReviewReport, onViewShip, onOpenUser, onClose }) {
  const [tab, setTab] = useState("ships");
  const [q, setQ] = useState("");

  const needle = q.trim().toLowerCase();
  const shipList = useMemo(
    () => ships.filter((s) => !needle || [s.title, s.submitted_by, s.type, s.owner, s.description].filter(Boolean).join(" ").toLowerCase().includes(needle)),
    [ships, needle]
  );
  const editList = useMemo(
    () => edits.filter((e) => !needle || [e.ship_title, e.submitted_by, e.note].filter(Boolean).join(" ").toLowerCase().includes(needle)),
    [edits, needle]
  );

  const reportList = useMemo(
    () => reports.filter((r) => !needle || [r.ship_title, r.reporter, r.note, REPORT_REASON_LABELS[r.reason]].filter(Boolean).join(" ").toLowerCase().includes(needle)),
    [reports, needle]
  );

  const total = ships.length + edits.length + reports.length;

  return (
    <SlidePanel
      title="Pending Review"
      subtitle={loaded ? (total ? `${total} item${total === 1 ? "" : "s"} waiting for you` : "Nothing waiting") : "Loading…"}
      onClose={onClose}
      wide
      actions={<button className="panel-icon-btn" onClick={onRefresh} title="Refresh" aria-label="Refresh">↻</button>}
    >
      <div className="panel-tabs" role="tablist">
        <button role="tab" aria-selected={tab === "ships"} className={`panel-tab${tab === "ships" ? " active" : ""}`} onClick={() => setTab("ships")}>New ships <span>{ships.length}</span></button>
        <button role="tab" aria-selected={tab === "edits"} className={`panel-tab${tab === "edits" ? " active" : ""}`} onClick={() => setTab("edits")}>Suggested edits <span>{edits.length}</span></button>
        <button role="tab" aria-selected={tab === "reports"} className={`panel-tab${tab === "reports" ? " active" : ""}`} onClick={() => setTab("reports")}>Reports <span>{reports.length}</span></button>
      </div>

      {total > 3 && <input className="panel-search" type="search" placeholder="Search title, user, type…" value={q} onChange={(e) => setQ(e.target.value)} />}

      {tab === "ships" && (
        <>
          {loaded && ships.length === 0 && (
            <div className="panel-empty"><div className="panel-empty-icon">✓</div><p>No new ships to review.</p></div>
          )}
          {ships.length > 0 && shipList.length === 0 && <p className="panel-muted">No matches.</p>}
          <div className="panel-list">
            {shipList.map((s) => (
              <PendingShip key={s.id} ship={s} busy={busyId === `ship-${s.id}`} onReview={onReviewShip} onOpenUser={onOpenUser} />
            ))}
          </div>
        </>
      )}

      {tab === "edits" && (
        <>
          {loaded && edits.length === 0 && (
            <div className="panel-empty"><div className="panel-empty-icon">✓</div><p>No suggested edits to review.</p></div>
          )}
          {edits.length > 0 && editList.length === 0 && <p className="panel-muted">No matches.</p>}
          <PendingEdits edits={editList} onReview={onReviewEdit} hideTitle />
        </>
      )}

      {tab === "reports" && (
        <>
          {loaded && reports.length === 0 && (
            <div className="panel-empty"><div className="panel-empty-icon">✓</div><p>No problem reports to review.</p></div>
          )}
          {reports.length > 0 && reportList.length === 0 && <p className="panel-muted">No matches.</p>}
          <div className="panel-list">
            {reportList.map((r) => (
              <ReportCard
                key={r.id}
                report={r}
                ship={mapShips.find((x) => Number(x.id) === Number(r.ship_id))}
                busy={busyId === `report-${r.id}`}
                onReview={onReviewReport}
                onViewShip={onViewShip}
                onOpenUser={onOpenUser}
              />
            ))}
          </div>
        </>
      )}
    </SlidePanel>
  );
}
